import CutwiseAPIClient, {
  METHOD_DELETE,
  METHOD_GET,
  METHOD_PATCH,
  METHOD_POST,
} from '@/common/network/CutwiseAPIClient';
import { GradeReferenceGroup } from '@/survey/entity/GradeReferenceGroup';
import { SurveyModel } from '@/survey/entity/Survey';
import { SurveyGroupedSortsModel } from '@/survey/entity/SurveyGroupedSorts';
import { SurveyResultData, SurveyResultMeta } from '@/survey/entity/SurveyResult';

export const PATH_SURVEY = 'api/survey';
export const PATH_CROWD_SURVEY = 'api/v1/crowd-survey';
export const PATH_SURVEY_RESULT = 'api/survey/result';
export const PATH_SURVEY_LIST = `${PATH_SURVEY}/object/collection`;
export const PATH_SURVEY_GRADES = 'api/survey/grades';
export const PATH_GROUPED_SORTS = 'v2/collections';
export const PATH_GRADE_REFERENCE_GROUPS = `${PATH_SURVEY}/references`;
export const PATH_SURVEY_RESULTS_CLAIM = 'api/v1/survey-results/claim';

export interface SaveSurveyResultBody {
  final: boolean;
  data: SurveyResultData;
  meta: Partial<SurveyResultMeta>;
  guestId?: string;
}

export default class SurveyAPI {
  createSurvey(body: SurveyModel) {
    const qb = CutwiseAPIClient.createQueryBuilder().method(METHOD_POST).path(PATH_SURVEY).body(body);

    const query = qb.getQuery();

    return CutwiseAPIClient.sendQuery(query);
  }

  editSurvey(id: string, body: SurveyModel) {
    const qb = CutwiseAPIClient.createQueryBuilder().method(METHOD_PATCH).path(`${PATH_SURVEY}/${id}`).body(body);

    const query = qb.getQuery();

    return CutwiseAPIClient.sendQuery(query);
  }

  deleteSurvey(id: string) {
    const qb = CutwiseAPIClient.createQueryBuilder().method(METHOD_DELETE).path(`${PATH_SURVEY}/${id}`);

    const query = qb.getQuery();

    return CutwiseAPIClient.sendQuery(query);
  }

  fetchSurveysByCollection(collectionId: string) {
    const qb = CutwiseAPIClient.createQueryBuilder().method(METHOD_GET).path(`${PATH_SURVEY_LIST}/${collectionId}`);

    const query = qb.getQuery();

    return CutwiseAPIClient.sendQuery<SurveyModel[]>(query);
  }

  fetchSurveyById(surveyId: string) {
    const qb = CutwiseAPIClient.createQueryBuilder().method(METHOD_GET).path(`${PATH_SURVEY}/${surveyId}`);

    const query = qb.getQuery();

    return CutwiseAPIClient.sendQuery<SurveyModel>(query);
  }

  fetchCrowdSurveyBatch(surveyId: string) {
    const qb = CutwiseAPIClient.createQueryBuilder().method(METHOD_GET).path(`${PATH_CROWD_SURVEY}/${surveyId}/next`);

    const query = qb.getQuery();

    return CutwiseAPIClient.sendQuery<SurveyModel>(query);
  }

  fetchSurveyResult(surveyId: string) {
    const qb = CutwiseAPIClient.createQueryBuilder().method(METHOD_GET).path(`${PATH_SURVEY_RESULT}/${surveyId}`);

    const query = qb.getQuery();

    return CutwiseAPIClient.sendQuery(query);
  }

  saveSurveyResult(surveyId: string, body: SaveSurveyResultBody) {
    const qb = CutwiseAPIClient.createQueryBuilder()
      .method(METHOD_POST)
      .path(`${PATH_SURVEY_RESULT}/${surveyId}`)
      .body(body);

    const query = qb.getQuery();

    return CutwiseAPIClient.sendQuery(query);
  }

  claimGuestResults(guestId: string) {
    const qb = CutwiseAPIClient.createQueryBuilder()
      .method(METHOD_POST)
      .path(PATH_SURVEY_RESULTS_CLAIM)
      .body({ guestId })
      .suppressErrorNotifications();

    const query = qb.getQuery();

    return CutwiseAPIClient.sendQuery(query);
  }

  fetchGradeScales() {
    const qb = CutwiseAPIClient.createQueryBuilder().method(METHOD_GET).path(PATH_SURVEY_GRADES);

    const query = qb.getQuery();

    return CutwiseAPIClient.sendQuery(query);
  }

  fetchGroupedSorts(collectionId: string, surveyId: string) {
    const qb = CutwiseAPIClient.createQueryBuilder()
      .method(METHOD_GET)
      .path(`${PATH_GROUPED_SORTS}/${collectionId}/sorts`)
      .addQueryStringParam('survey', surveyId);

    const query = qb.getQuery();

    return CutwiseAPIClient.sendQuery<SurveyGroupedSortsModel[]>(query);
  }

  async fetchGradeReferenceGroups() {
    const qb = CutwiseAPIClient.createQueryBuilder().method(METHOD_GET).path(PATH_GRADE_REFERENCE_GROUPS);

    const query = qb.getQuery();
    const res = await CutwiseAPIClient.sendQuery<GradeReferenceGroup[]>(query);

    return res.content;
  }
}
